import { z } from 'zod';
/**
 * Vitrine do bairro (G3): páginas de negócios montadas com o que o próprio site publica.
 * Dois níveis: com preço (loja online) e sem preço (o que vende, horário e telefone).
 */
export const VITRINE_STATUSES = [
    'fila',
    'coletando',
    'ok',
    'fora_do_ar',
    'robots_fecha',
    'erro',
    'retirado',
    /** Domínio à venda, conteúdo injetado ou redirecionamento escondido: nunca entra. */
    'suspeito',
    /** Nenhum telefone do Espírito Santo: fábrica ou empresa de fora, não loja do bairro. */
    'fora_da_area',
];
export const VITRINE_REVIEWS = ['pendente', 'aprovado', 'recusado'];
/** Preço visto há mais que isso some da página (CDC: oferta precisa estar valendo). */
export const VITRINE_PRICE_MAX_DAYS = 30;
export const vitrineProductSchema = z.object({
    name: z.string().min(1).max(120),
    priceCents: z.number().int().min(0).max(1_000_000_000).nullable(),
    currency: z.string().max(3).nullable(),
    available: z.boolean().nullable(),
    category: z.string().max(80).nullable(),
    url: z.string().max(300).nullable(),
});
/** O que o coletor devolve de um site. */
export const vitrineResultSchema = z.object({
    placeId: z.string().min(1).max(80),
    status: z.enum(['ok', 'fora_do_ar', 'robots_fecha', 'erro', 'suspeito']),
    error: z.string().max(120).nullable().optional(),
    business: z.object({
        name: z.string().max(120).nullable(),
        telephone: z.string().max(40).nullable(),
        openingHours: z.array(z.string().max(80)).max(14),
    }),
    products: z.array(vitrineProductSchema).max(200),
    pagesRead: z.number().int().min(0).max(100),
    /** Resumo da página inicial: só para o modelo dizer "o que vende" (nada vai copiado). */
    summary: z.string().max(2000).optional(),
    /** Telefones achados no site, com o texto em volta (o servidor escolhe o da unidade). */
    phones: z
        .array(z.object({
        digits: z.string().regex(/^\d{10,11}$/),
        whatsapp: z.boolean(),
        context: z.string().max(100),
    }))
        .max(20)
        .optional(),
    /** O endereço final abriu em https no mesmo domínio: o link do site pode ser oferecido. */
    secure: z.boolean().optional(),
    /** Onde o site abriu de fato (a loja pode ter trocado de domínio). */
    finalUrl: z.string().max(300).optional(),
});
export const vitrineJobSchema = z.object({
    placeId: z.string(),
    siteUrl: z.string(),
    /** O nome na base: o coletor reconhece quando a loja trocou de domínio. */
    name: z.string().optional(),
});
/** Linha da conferência na sala do dono. */
export const vitrineReviewRowSchema = z.object({
    placeId: z.string(),
    name: z.string(),
    domain: z.string(),
    siteUrl: z.string(),
    city: z.string(),
    group: z.string(),
    status: z.enum(VITRINE_STATUSES),
    level: z.enum(['com_preco', 'sem_preco']).nullable(),
    review: z.enum(VITRINE_REVIEWS),
    telephone: z.string().nullable(),
    products: z.number().int(),
    sample: z.array(z.object({ name: z.string(), priceCents: z.number().int().nullable() })),
    /** Sem preço no site: o que a loja vende, nas palavras do modelo (até 8). */
    offer: z.array(z.string()).default([]),
    about: z.string().nullable().default(null),
    /** Aprovada sozinha pelos critérios da rede base (o dono pode recusar). */
    auto: z.boolean().default(false),
    /** Para quem vende, segundo o modelo: pessoas, empresas ou ambos. */
    audience: z.enum(['pessoas', 'empresas', 'ambos']).nullable().default(null),
    crawledAt: z.iso.datetime().nullable(),
});
export const vitrineDonoSchema = z.object({
    counts: z.record(z.string(), z.number().int()),
    rows: z.array(vitrineReviewRowSchema),
});
/** Procurados (G6): lugares pedidos 3 vezes ou mais no mês pelo Perto, para convidar à mão. */
export const WANTED_MIN_ASKS = 3;
export const donoWantedSchema = z.object({
    month: z.string(),
    rows: z.array(z.object({
        placeId: z.string(),
        name: z.string(),
        label: z.string(),
        city: z.string(),
        phone: z.string().nullable(),
        asks: z.number().int(),
        /** Já está no Secretário (assumiu a vitrine ou tem o perfil ligado). */
        onSec: z.boolean(),
    })),
});
/** Entrada pelo negócio (estudo da cortesia, C2): a medição de 60 dias e o gatilho do C3. */
export const donoLinkedSchema = z.object({
    days: z.number().int(),
    views: z.object({ visita: z.number().int(), consulta: z.number().int() }),
    accounts: z.number().int(),
    organic: z.number().int(),
    d30Linked: z.number().int().nullable(),
    d30Organic: z.number().int().nullable(),
    paidLinked: z.number().int(),
    paidOrganic: z.number().int(),
    trigger: z.object({ accounts: z.number().int(), reached: z.boolean() }),
});
/** O convite à mão, com a finalidade explícita: só o SIM põe o lugar no catálogo. */
export function wantedInviteText(name) {
    return `Olá! Aqui é o Igor, do Secretário. Vizinhos de vocês têm pedido ${name} pelo app. Querem receber esses pedidos pelo Secretário, sem custo? Se sim, responda SIM.`;
}
/** A página pública de um negócio da vitrine (não administrada pela loja). */
export const vitrinePageSchema = z.object({
    placeId: z.string(),
    name: z.string(),
    group: z.string(),
    city: z.string(),
    /** Só quando o site foi conferido (https, mesmo domínio, sem sinal de risco). */
    siteUrl: z.string().nullable(),
    telephone: z.string().nullable(),
    /** O número atende pelo WhatsApp (o site liga a ele), mesmo quando é fixo. */
    whatsapp: z.boolean().default(false),
    openingHours: z.array(z.string()),
    level: z.enum(['com_preco', 'sem_preco']),
    products: z.array(z.object({
        name: z.string(),
        priceCents: z.number().int().nullable(),
        available: z.boolean().nullable(),
        url: z.string().nullable(),
    })),
    /** Sem preço no site: o que a loja vende (até 8 categorias) e uma frase, sem texto copiado. */
    offer: z.array(z.string()).default([]),
    about: z.string().nullable().default(null),
    /** Quando o site foi lido (os preços são "vistos em" esta data). */
    seenAt: z.iso.datetime(),
    /** A loja já assumiu a página (confirmou pelo código no celular dela). */
    claimed: z.boolean().default(false),
});
/** Assumir a página: o pedido e os 4 últimos dígitos do celular que recebeu o código. */
export const vitrineClaimStartSchema = z.object({ claimId: z.uuid(), phoneEnd: z.string() });
export const vitrineClaimVerifySchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/),
});
export const vitrineClaimDoneSchema = z.object({
    placeId: z.string(),
    /** O Meu negócio nasceu agora (quem já tinha fica com o dele). */
    createdBusiness: z.boolean().default(false),
});
